import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import debounce from "lodash/debounce";
import { format } from "date-fns";
import { useAtomValue } from "jotai";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BarChart3,
  Calendar as CalendarIcon,
  X,
} from "lucide-react";

import { Routes, getRoute } from "~/routes";
import { encodeTestIds } from "./comparison/utils";
import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { PasswordSetupBanner } from "@modules/authentication/PasswordSetupBanner";
import {
  useFarmsLazyQuery,
  useFieldsForFarmLazyQuery,
  useSearchPesTestsQuery,
  useCreateReportMutation,
} from "generated/hooks";
import { fetchAllPages, useFetchAll } from "~/hooks/useFetchAll";
import {
  PesTestType,
  ReportFormat,
  SortEnumType,
  PesTestSortInput,
  PesTestFilterInput,
} from "generated/graphql";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Checkbox } from "~/components/ui/checkbox";
import { Badge } from "~/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "~/components/ui/pagination";
import {
  SelectionCombobox,
  SelectionComboboxOption,
} from "~/components/ui/selection-combobox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { Calendar } from "~/components/ui/calendar";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import { selectedOrganisationIdAtom } from "~/state/organisation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { toast } from "sonner";

interface PesTest {
  id: string;
  utc_test_time: string;
  created_at: string;
  name: string;
  test_type: "Soil Test" | "Sensor Check";
  created_by: {
    full_name: string;
  };
  is_failure: boolean;
  is_excluded_from_ml: boolean;
  sample_file_url: string | null;
  soil_sample?: {
    name: string;
  };
  organisation: {
    name: string;
    id: string;
  };
  farm: {
    name: string;
    id: string;
  };
  field: {
    name: string;
    id: string;
  };
}

const SORT_KEYS = ["Date", "Name", "farm", "field"] as const;
type SortKey = (typeof SORT_KEYS)[number];

const isSortKey = (value: string): value is SortKey =>
  SORT_KEYS.includes(value as SortKey);

const parseSortKey = (value: string | null): SortKey => {
  const normalized = value === "utc_test_time" ? "Date" : value ?? "Date";
  return isSortKey(normalized) ? normalized : "Date";
};

const SORT_KEY_TO_ORDER_FIELD: Record<
  SortKey,
  (direction: SortEnumType) => PesTestSortInput
> = {
  Date: (direction) => ({ utcTestTime: direction }),
  Name: (direction) => ({ name: direction }),
  farm: (direction) => ({ farm: { name: direction } }),
  field: (direction) => ({ field: { name: direction } }),
};

type MultiSelectOption = SelectionComboboxOption;

const arraysEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const optionsEqual = (a: MultiSelectOption[], b: MultiSelectOption[]) =>
  a.length === b.length &&
  a.every((option, index) => option.value === b[index]?.value);

interface SearchParamsSnapshot {
  searchTerm: string;
  sortKeyParam: SortKey;
  sortDirectionParam: "ASC" | "DESC";
  selectedFarms: string[];
  selectedFields: string[];
  fromDateSelected: boolean;
  toDateSelected: boolean;
  dateRange: { from: Date; to: Date };
}

const getDefaultFromDate = () => new Date("2024-09-01");
const parseDateOr = (value: string | null, fallbackFactory: () => Date) => {
  if (!value) {
    return fallbackFactory();
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallbackFactory() : parsed;
};

const parseCsvParam = (value: string | null) =>
  value?.split(",").filter(Boolean) ?? [];

const mapOptionsToValues = (options: MultiSelectOption[]) =>
  options
    .map((option) => option.value)
    .filter((value): value is string => Boolean(value));

const cloneOptions = (options: MultiSelectOption[]) =>
  options.map((option) => ({ ...option }));

const buildOptions = <T,>(
  items: T[] | null | undefined,
  getValue: (item: T) => string,
  getLabel: (item: T) => string
): MultiSelectOption[] => {
  const mappedOptions =
    items?.map((item) => ({
      value: getValue(item),
      label: getLabel(item),
    })) ?? [];

  return Array.from(
    new Map(mappedOptions.map((option) => [option.value, option])).values()
  );
};

const inflateOptions = (
  ids: string[],
  previous: MultiSelectOption[],
  available: MultiSelectOption[]
) => {
  const next = ids.map((id) => {
    const fromAvailable = available.find((option) => option.value === id);
    if (fromAvailable) {
      return fromAvailable;
    }
    const fromPrevious = previous.find((option) => option.value === id);
    if (fromPrevious) {
      return fromPrevious;
    }
    return { value: id, label: "" };
  });
  return optionsEqual(previous, next) ? previous : next;
};

const resolveSelectedOptions = (
  ids: string[],
  options: MultiSelectOption[],
  fallback?: (id: string) => MultiSelectOption | null
) =>
  ids
    .map(
      (id) => options.find((option) => option.value === id) ?? fallback?.(id)
    )
    .filter((option): option is MultiSelectOption =>
      Boolean(option && option.value)
    );

type IdListFilterKey = "selectedFarms" | "selectedFields";

type ConfigState<C extends FilterConfig> = string[];

type IdListFilterConfig = {
  kind: "idList";
  key: IdListFilterKey;
  param: string;
  setter: Dispatch<SetStateAction<string[]>>;
  options: MultiSelectOption[];
  fallback?: (id: string) => MultiSelectOption | null;
};

type FilterConfig = IdListFilterConfig;

const resolveFilterSelections = (
  config: FilterConfig,
  state: string[]
): MultiSelectOption[] => {
  return resolveSelectedOptions(state, config.options, config.fallback);
};

interface FilterMultiSelectProps {
  id: string;
  label: string;
  placeholder: string;
  options: SelectionComboboxOption[];
  value: MultiSelectOption[];
  onChange: (next: MultiSelectOption[]) => void;
}

const FilterMultiSelect: React.FC<FilterMultiSelectProps> = ({
  id,
  label,
  placeholder,
  options,
  value,
  onChange,
}) => {
  const selectedValues = value.map((item) => item.value);
  const availableOptions = options.filter(
    (option) => !selectedValues.includes(option.value)
  );

  const handleSelect = (selectedValue: string) => {
    if (value.some((option) => option.value === selectedValue)) {
      return;
    }
    const selectedOption = options.find(
      (option) => option.value === selectedValue
    );
    if (!selectedOption) {
      return;
    }
    onChange([...value, selectedOption]);
  };

  const handleRemove = (selectedValue: string) => {
    onChange(value.filter((option) => option.value !== selectedValue));
  };

  return (
    <div className="space-y-2 w-full">
      <Label htmlFor={id} className="text-sm font-semibold text-foreground">
        {label}
      </Label>
      <SelectionCombobox
        placeholder={placeholder}
        options={availableOptions}
        emptyMessage="No results"
        onSelect={handleSelect}
        triggerClassName="h-9 w-full justify-between rounded-md border bg-background text-sm"
        contentClassName="w-[260px]"
      />
      <div className="flex flex-wrap gap-2">
        {value.length > 0 ? (
          value.map((option) => (
            <Badge
              key={option.value}
              variant="secondary"
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
            >
              <span className="max-w-[160px] truncate">{option.label}</span>
              <button
                type="button"
                className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => handleRemove(option.value)}
                aria-label={`Remove ${option.label}`}
              >
                <X className="size-3" aria-hidden="true" />
              </button>
            </Badge>
          ))
        ) : (
          <p className="text-xs text-muted-foreground">No selections</p>
        )}
      </div>
    </div>
  );
};

interface DatePickerFieldProps {
  id: string;
  label: string;
  selectedDate: Date;
  placeholder: string;
  isActive: boolean;
  onSelect: (date: Date) => void;
  onClear: () => void;
}

const DatePickerField: React.FC<DatePickerFieldProps> = ({
  id,
  label,
  selectedDate,
  placeholder,
  isActive,
  onSelect,
  onClear,
}) => {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-semibold text-foreground">
        {label}
      </Label>
      <div className="flex items-center gap-2">
        <Popover>
          <PopoverTrigger>
            <Button
              id={id}
              variant="outline"
              className={cn(
                "inline-flex h-9 w-full items-center justify-start gap-2 rounded-md border bg-background text-left text-sm font-normal",
                !isActive && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="size-4" />
              <span>
                {isActive ? format(selectedDate, "dd/MM/yyyy") : placeholder}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(nextDate) => {
                if (nextDate) {
                  onSelect(nextDate);
                }
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        {isActive && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={onClear}
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
};

const UserDashboard = () => {
  const navigate = useNavigate();
  const selectedOrganisationId = useAtomValue(selectedOrganisationIdAtom);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearchTerm = searchParams.get("search") ?? "";
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);

  const normalizedInitialSortKey = parseSortKey(searchParams.get("sort"));
  const initialSortDirectionParam =
    (searchParams.get("direction") as "ASC" | "DESC") ?? "DESC";
  const [sortKeyParam, setSortKeyParam] = useState<SortKey>(
    normalizedInitialSortKey
  );
  const [sortDirectionParam, setSortDirectionParam] = useState<"ASC" | "DESC">(
    initialSortDirectionParam
  );
  // Keep existing sort UI, but map it to HotChocolate order input.

  const initialFarms =
    searchParams.get("farms")?.split(",").filter(Boolean) ?? [];
  const initialFields =
    searchParams.get("fields")?.split(",").filter(Boolean) ?? [];

  const [selectedFarms, setSelectedFarms] = useState<string[]>(initialFarms);
  const [selectedFields, setSelectedFields] = useState<string[]>(initialFields);

  const urlFromDate = searchParams.get("fromDate");
  const urlToDate = searchParams.get("toDate");
  const [fromDateSelected, setFromDateSelected] = useState(
    Boolean(urlFromDate)
  );
  const [toDateSelected, setToDateSelected] = useState(Boolean(urlToDate));
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>(() => ({
    from: parseDateOr(urlFromDate, getDefaultFromDate),
    to: parseDateOr(urlToDate, () => new Date()),
  }));
  const defaultToDateRef = useRef<Date>(new Date(dateRange.to));

  const [collapsed, setCollapsed] = useState(true);
  const [selectedTests, setSelectedTests] = useState<PesTest[]>([]);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<ReportFormat>(
    ReportFormat.Pdf
  );

  const [page, setPage] = useState(0);
  const [pageCursors, setPageCursors] = useState<Map<number, string | null>>(
    new Map([[0, null]])
  );

  const updateSearchParams = useCallback(
    (overrides?: Partial<SearchParamsSnapshot>) => {
      const {
        searchTerm: overrideSearchTerm,
        sortKeyParam: overrideSortKeyParam,
        sortDirectionParam: overrideSortDirectionParam,
        selectedFarms: overrideSelectedFarms,
        selectedFields: overrideSelectedFields,
        fromDateSelected: overrideFromDateSelected,
        toDateSelected: overrideToDateSelected,
        dateRange: overrideDateRange,
      } = overrides ?? {};

      const nextSearchTerm =
        overrideSearchTerm !== undefined ? overrideSearchTerm : searchTerm;
      const nextSortKeyParam =
        overrideSortKeyParam !== undefined
          ? overrideSortKeyParam
          : sortKeyParam;
      const nextSortDirectionParam =
        overrideSortDirectionParam !== undefined
          ? overrideSortDirectionParam
          : sortDirectionParam;
      const nextSelectedFarms =
        overrideSelectedFarms !== undefined
          ? overrideSelectedFarms
          : selectedFarms;
      const nextSelectedFields =
        overrideSelectedFields !== undefined
          ? overrideSelectedFields
          : selectedFields;
      const nextFromDateSelected =
        overrideFromDateSelected !== undefined
          ? overrideFromDateSelected
          : fromDateSelected;
      const nextToDateSelected =
        overrideToDateSelected !== undefined
          ? overrideToDateSelected
          : toDateSelected;
      const nextDateRange =
        overrideDateRange !== undefined ? overrideDateRange : dateRange;

      const params = new URLSearchParams(searchParams);
      params.set("sort", nextSortKeyParam);
      params.set("direction", nextSortDirectionParam);
      params.delete("page");

      if (nextSearchTerm) {
        params.set("search", nextSearchTerm);
      } else {
        params.delete("search");
      }

      if (nextSelectedFarms.length > 0) {
        params.set("farms", nextSelectedFarms.join(","));
      } else {
        params.delete("farms");
      }

      if (nextSelectedFields.length > 0) {
        params.set("fields", nextSelectedFields.join(","));
      } else {
        params.delete("fields");
      }

      if (nextFromDateSelected && nextDateRange) {
        params.set("fromDate", format(nextDateRange.from, "yyyy-MM-dd"));
      } else {
        params.delete("fromDate");
      }

      if (nextToDateSelected && nextDateRange) {
        params.set("toDate", format(nextDateRange.to, "yyyy-MM-dd"));
      } else {
        params.delete("toDate");
      }

      if (params.toString() === searchParams.toString()) {
        return;
      }

      setSearchParams(params);
    },
    [
      searchParams,
      searchTerm,
      sortKeyParam,
      sortDirectionParam,
      selectedFarms,
      selectedFields,
      fromDateSelected,
      toDateSelected,
      dateRange,
      setSearchParams,
    ]
  );

  const updateSearchTermParam = useCallback(
    (value: string) => {
      setSearchParams((params) => {
        const next = new URLSearchParams(params);
        next.delete("page");
        if (value) {
          next.set("search", value);
        } else {
          next.delete("search");
        }
        return next;
      });
    },
    [setSearchParams]
  );

  const debouncedUpdateSearch = useMemo(
    () => debounce(updateSearchTermParam, 500),
    [updateSearchTermParam]
  );

  useEffect(
    () => () => debouncedUpdateSearch.cancel(),
    [debouncedUpdateSearch]
  );

  const [fetchFarms] = useFarmsLazyQuery();
  const { data: allFarms } = useFetchAll(fetchFarms, (d) => d.farms, {
    skip: !selectedOrganisationId,
    variables: { after: null },
  });

  const [fetchFieldsForFarm] = useFieldsForFarmLazyQuery();
  const [fieldsData, setFieldsData] = useState<
    Array<{ id: string; name: string; farmId: string }>
  >([]);

  useEffect(() => {
    let disposed = false;

    const fetchFields = async () => {
      if (selectedFarms.length === 0) {
        setFieldsData([]);
        return;
      }

      const allFields: Array<{ id: string; name: string; farmId: string }> = [];

      try {
        for (const farmId of selectedFarms) {
          const nodes = await fetchAllPages(
            fetchFieldsForFarm,
            (data) => data.farm?.fields,
            {
              variables: { farmId, after: null },
            }
          );
          allFields.push(
            ...nodes.map((field) => ({
              id: field.id,
              name: field.name,
              farmId,
            }))
          );
        }

        const uniqueFields = Array.from(
          new Map(allFields.map((field) => [field.id, field])).values()
        );

        if (!disposed) {
          setFieldsData(uniqueFields);
        }
      } catch (error) {
        console.error("Failed to load fields", error);
        if (!disposed) {
          setFieldsData([]);
        }
      }
    };

    void fetchFields();

    return () => {
      disposed = true;
    };
  }, [selectedFarms, fetchFieldsForFarm]);

  const { data, loading, error, refetch } = useSearchPesTestsQuery({
    variables: {
      after: pageCursors.get(page) ?? null,
      where: {
        createdAt: {
          gte: new Date(
            `${dateRange.from.toISOString().split("T")[0]}T00:00:00.000Z`
          ).toISOString(),
          lte: new Date(
            `${dateRange.to.toISOString().split("T")[0]}T23:59:59.999Z`
          ).toISOString(),
        },
        ...(searchParams.get("search")
          ? { name: { contains: searchParams.get("search") ?? undefined } }
          : {}),
        ...(selectedOrganisationId
          ? ({ organisation: { id: { eq: selectedOrganisationId } } } as Record<
              string,
              unknown
            >)
          : {}),
        testType: { eq: PesTestType.SoilTest },
      } as PesTestFilterInput,
      order: [
        (SORT_KEY_TO_ORDER_FIELD[sortKeyParam] ?? SORT_KEY_TO_ORDER_FIELD.Date)(
          sortDirectionParam === "ASC" ? SortEnumType.Asc : SortEnumType.Desc
        ),
      ],
    },
    skip: !selectedOrganisationId,
    fetchPolicy: "cache-and-network",
  });

  const [createReport, { loading: creatingReport }] = useCreateReportMutation();

  const transformedTests: PesTest[] =
    data?.pesTests?.nodes?.map((test) => ({
      id: test.id,
      utc_test_time: test.utcTestTime,
      created_at: test.createdAt,
      name: test.name,
      test_type:
        test.testType === "SOIL_TEST"
          ? "Soil Test"
          : ("Sensor Check" as "Soil Test" | "Sensor Check"),
      created_by: {
        full_name: "unknown",
      },
      is_failure: false,
      is_excluded_from_ml: false,
      sample_file_url: null,
      soil_sample: undefined,
      organisation: {
        name: "unknown",
        id: "",
      },
      farm: {
        name: test.farm?.name ?? "unknown",
        id: test.farm?.id,
      },
      field: {
        name: test.field?.name ?? "unknown",
        id: test.field?.id,
      },
    })) ?? [];

  const hasNextPage = data?.pesTests?.pageInfo?.hasNextPage ?? false;
  const nextCursor = data?.pesTests?.pageInfo?.endCursor ?? null;

  useEffect(() => {
    if (!hasNextPage || !nextCursor) {
      return;
    }
    setPageCursors((prev) => {
      if (prev.get(page + 1) === nextCursor) {
        return prev;
      }
      const next = new Map(prev);
      next.set(page + 1, nextCursor);
      return next;
    });
  }, [hasNextPage, nextCursor, page]);

  const farmOptions = useMemo(
    () =>
      buildOptions(
        allFarms.filter(
          (farm) => farm.organisation?.id === selectedOrganisationId
        ),
        (farm) => farm.id,
        (farm) => farm.name
      ),
    [allFarms, selectedOrganisationId]
  );

  const fieldOptions = useMemo(() => {
    return buildOptions(
      fieldsData,
      (field) => field.id,
      (field) => field.name
    );
  }, [fieldsData]);

  const filterConfigs = useMemo<FilterConfig[]>(
    () => [
      {
        kind: "idList",
        key: "selectedFarms",
        param: "farms",
        setter: setSelectedFarms,
        options: farmOptions,
      },
      {
        kind: "idList",
        key: "selectedFields",
        param: "fields",
        setter: setSelectedFields,
        options: fieldOptions,
      },
    ],
    [farmOptions, fieldOptions]
  );

  const getFilterConfig = (key: IdListFilterKey): FilterConfig => {
    const config = filterConfigs.find((item) => item.key === key);
    if (!config) {
      throw new Error(`Missing filter configuration for ${key}`);
    }
    return config;
  };

  const resetPagination = () => {
    setPage(0);
    setPageCursors(new Map([[0, null]]));
  };

  const createListFilterHandler = (config: FilterConfig) => {
    return (selectedOptions: MultiSelectOption[]) => {
      const nextState = mapOptionsToValues(selectedOptions);
      config.setter(nextState);
      resetPagination();
      updateSearchParams({
        [config.key]: nextState,
      } as Partial<SearchParamsSnapshot>);
      void refetch();
    };
  };

  useEffect(() => {
    const nextSearch = searchParams.get("search") ?? "";
    setSearchTerm((prev) => (prev === nextSearch ? prev : nextSearch));

    const normalizedSortKey = parseSortKey(searchParams.get("sort"));
    if (normalizedSortKey !== sortKeyParam) {
      setSortKeyParam(normalizedSortKey);
    }

    const rawDirection =
      (searchParams.get("direction") as "ASC" | "DESC") ?? "DESC";
    if (rawDirection !== sortDirectionParam) {
      setSortDirectionParam(rawDirection);
    }

    filterConfigs.forEach((config) => {
      const nextIds = parseCsvParam(searchParams.get(config.param));
      config.setter((prev) => (arraysEqual(prev, nextIds) ? prev : nextIds));
    });

    const fromDateParam = searchParams.get("fromDate");
    const toDateParam = searchParams.get("toDate");
    const hasFromDate = Boolean(fromDateParam);
    const hasToDate = Boolean(toDateParam);

    setFromDateSelected((prev) => (prev === hasFromDate ? prev : hasFromDate));
    setToDateSelected((prev) => (prev === hasToDate ? prev : hasToDate));

    setDateRange((prev) => {
      let nextFrom = prev.from;
      if (hasFromDate && fromDateParam) {
        const parsedFrom = new Date(fromDateParam);
        if (!Number.isNaN(parsedFrom.getTime())) {
          nextFrom = parsedFrom;
        } else {
          nextFrom = getDefaultFromDate();
        }
      } else if (!hasFromDate) {
        nextFrom = getDefaultFromDate();
      }

      let nextTo = prev.to;
      if (hasToDate && toDateParam) {
        const parsedTo = new Date(toDateParam);
        if (!Number.isNaN(parsedTo.getTime())) {
          nextTo = parsedTo;
        } else {
          nextTo = new Date(defaultToDateRef.current);
        }
      } else if (!hasToDate) {
        nextTo = new Date(defaultToDateRef.current);
      }

      if (
        prev.from.getTime() === nextFrom.getTime() &&
        prev.to.getTime() === nextTo.getTime()
      ) {
        return prev;
      }

      return { from: nextFrom, to: nextTo };
    });
  }, [searchParams, sortKeyParam, sortDirectionParam, filterConfigs]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    resetPagination();
    debouncedUpdateSearch(value);
  };

  const farmFilterConfig = getFilterConfig("selectedFarms");
  const fieldFilterConfig = getFilterConfig("selectedFields");

  const handleFarmChange = (selectedOptions: MultiSelectOption[]) => {
    const nextFarms = mapOptionsToValues(selectedOptions);

    // Clear selected fields that no longer belong to the selected farms
    const validFieldIds = new Set(
      fieldsData.filter((f) => nextFarms.includes(f.farmId)).map((f) => f.id)
    );
    const nextFields = selectedFields.filter((id) => validFieldIds.has(id));

    setSelectedFarms(nextFarms);
    setSelectedFields(nextFields);

    resetPagination();
    updateSearchParams({
      selectedFarms: nextFarms,
      selectedFields: nextFields,
    });
    void refetch();
  };

  const handleFieldChange = createListFilterHandler(fieldFilterConfig);

  const farmSelections = resolveFilterSelections(
    farmFilterConfig,
    selectedFarms
  );
  const fieldSelections = resolveFilterSelections(
    fieldFilterConfig,
    selectedFields
  );

  const handleFromDateChange = (nextFrom: Date) => {
    const adjustedTo = dateRange.to < nextFrom ? nextFrom : dateRange.to;
    const nextRange = { from: nextFrom, to: adjustedTo };
    setDateRange(nextRange);
    setFromDateSelected(true);
    if (!toDateSelected) {
      defaultToDateRef.current = nextRange.to;
    }
    resetPagination();
    updateSearchParams({
      dateRange: nextRange,
      fromDateSelected: true,
    });
    void refetch();
  };

  const handleToDateChange = (nextTo: Date) => {
    const nextRange = { from: dateRange.from, to: nextTo };
    setDateRange(nextRange);
    setToDateSelected(true);
    resetPagination();
    updateSearchParams({
      dateRange: nextRange,
      toDateSelected: true,
    });
    void refetch();
  };

  const handleFromDateClear = () => {
    setFromDateSelected(false);
    const fallbackFrom = getDefaultFromDate();
    const nextRange = { ...dateRange, from: fallbackFrom };
    setDateRange(nextRange);
    if (!toDateSelected) {
      defaultToDateRef.current = nextRange.to;
    }
    resetPagination();
    updateSearchParams({
      dateRange: nextRange,
      fromDateSelected: false,
    });
    void refetch();
  };

  const handleToDateClear = () => {
    setToDateSelected(false);
    const fallbackTo = new Date();
    const nextRange = { ...dateRange, to: fallbackTo };
    setDateRange(nextRange);
    defaultToDateRef.current = fallbackTo;
    resetPagination();
    updateSearchParams({
      dateRange: nextRange,
      toDateSelected: false,
    });
    void refetch();
  };

  const resetFilters = () => {
    debouncedUpdateSearch.cancel();
    const resetRange = {
      from: getDefaultFromDate(),
      to: new Date(),
    };
    setSearchTerm("");
    setSelectedFarms([]);
    setSelectedFields([]);
    setDateRange(resetRange);
    setFromDateSelected(false);
    setToDateSelected(false);
    defaultToDateRef.current = resetRange.to;
    setSelectedTests([]);
    resetPagination();
    setSortKeyParam("Date");
    setSortDirectionParam("DESC");
    updateSearchParams({
      searchTerm: "",
      selectedFarms: [],
      selectedFields: [],
      dateRange: resetRange,
      fromDateSelected: false,
      toDateSelected: false,
      sortKeyParam: "Date",
      sortDirectionParam: "DESC",
    });
    void refetch();
  };

  const handleTestSelection = (test: PesTest) => {
    setSelectedTests((prev) => {
      if (prev.some((selected) => selected.id === test.id)) {
        return prev.filter((selected) => selected.id !== test.id);
      }
      return [...prev, test];
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const testsToAdd = transformedTests.filter(
        (test) => !selectedTestIds.has(test.id)
      );
      setSelectedTests((prev) => [...prev, ...testsToAdd]);
    } else {
      setSelectedTests((prev) =>
        prev.filter(
          (test) => !transformedTests.some((row) => row.id === test.id)
        )
      );
    }
  };

  const handleSort = (key: SortKey) => {
    const nextDirection: "ASC" | "DESC" =
      sortKeyParam === key && sortDirectionParam === "ASC" ? "DESC" : "ASC";

    setSortKeyParam(key);
    setSortDirectionParam(nextDirection);
    resetPagination();
    updateSearchParams({
      sortKeyParam: key,
      sortDirectionParam: nextDirection,
    });
    void refetch();
  };

  const renderSortIcon = (key: SortKey) => {
    if (sortKeyParam !== key) {
      return (
        <ArrowUpDown
          className="size-4 text-muted-foreground"
          aria-hidden="true"
        />
      );
    }
    return sortDirectionParam === "ASC" ? (
      <ArrowUp className="size-4" aria-hidden="true" />
    ) : (
      <ArrowDown className="size-4" aria-hidden="true" />
    );
  };

  const getActiveFiltersCount = () => {
    return (
      Number(selectedFarms.length > 0) +
      Number(selectedFields.length > 0) +
      Number(Boolean(searchTerm)) +
      Number(fromDateSelected) +
      Number(toDateSelected)
    );
  };

  const activeFiltersCount = getActiveFiltersCount();
  const selectedTestIds = useMemo(
    () => new Set(selectedTests.map((test) => test.id)),
    [selectedTests]
  );
  const isAllSelected =
    transformedTests.length > 0 &&
    transformedTests.every((test) => selectedTestIds.has(test.id));

  const handleExportClick = () => {
    setIsExportModalOpen(true);
  };

  const handleCompareClick = () => {
    const testIds = selectedTests.map((t) => t.id);
    const encoded = encodeTestIds(testIds);
    navigate(`${Routes.UserTestCompare}?tests=${encoded}`);
  };

  const confirmExport = async () => {
    try {
      const { data } = await createReport({
        variables: {
          input: {
            pesTestIds: selectedTests.map((t) => t.id),
            // Backend supports this; codegen schema may lag behind running server.
            format: exportFormat,
          } as any,
        },
      });

      if (data?.createReport.id) {
        toast.success("Export initiated successfully.");
        setIsExportModalOpen(false);
        setSelectedTests([]);
      } else {
        toast.error("Failed to start export.");
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("An error occurred during export.");
    }
  };

  return (
    <PageTemplate
      title="My Tests"
      activeItem="My Tests"
      breadcrumbs={[{ label: "My Tests", path: "/dashboard" }]}
      actions={
        selectedTests.length > 0
          ? [
              ...(selectedTests.length >= 2
                ? [
                    {
                      text: `Compare (${selectedTests.length})`,
                      action: handleCompareClick,
                      disabled: false,
                    },
                  ]
                : []),
              {
                text: `Export (${selectedTests.length})`,
                action: handleExportClick,
                disabled: false,
              },
            ]
          : []
      }
    >
      <div className="flex flex-col gap-6">
        <PasswordSetupBanner />
        <Card className="overflow-hidden gap-0">
          <CardHeader className="flex flex-col gap-3 pb-0 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-base font-semibold text-foreground">
              Filters
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCollapsed((prev) => !prev)}
              >
                <span>
                  {collapsed ? "Show filters" : "Hide filters"}
                  {activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ""}
                </span>
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={resetFilters}
                disabled={activeFiltersCount === 0}
              >
                Reset
              </Button>
            </div>
          </CardHeader>
          {!collapsed && (
            <CardContent className="grid grid-cols-1 gap-x-6 gap-y-4 pb-0 pt-6 sm:grid-cols-2 lg:grid-cols-3">
              <div className="w-full space-y-1.5">
                <FilterMultiSelect
                  id="farm-filter"
                  label="Farm"
                  placeholder="Select farm..."
                  options={farmOptions}
                  value={farmSelections}
                  onChange={handleFarmChange}
                />
              </div>
              {selectedFarms.length > 0 && (
                <div className="w-full space-y-1.5">
                  <FilterMultiSelect
                    id="field-filter"
                    label="Field"
                    placeholder="Select field..."
                    options={fieldOptions}
                    value={fieldSelections}
                    onChange={handleFieldChange}
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 w-full sm:col-span-2 lg:col-span-3">
                <div className="w-full space-y-1.5">
                  <DatePickerField
                    id="from-date-filter"
                    label="From date"
                    placeholder="Select date"
                    selectedDate={dateRange.from}
                    isActive={fromDateSelected}
                    onSelect={handleFromDateChange}
                    onClear={handleFromDateClear}
                  />
                </div>
                <div className="w-full space-y-1.5">
                  <DatePickerField
                    id="to-date-filter"
                    label="To date"
                    placeholder="Select date"
                    selectedDate={dateRange.to}
                    isActive={toDateSelected}
                    onSelect={handleToDateChange}
                    onClear={handleToDateClear}
                  />
                </div>
              </div>
              <div className="w-full space-y-2 sm:col-span-2 lg:col-span-3">
                <Label
                  htmlFor="pes-tests-search"
                  className="text-sm font-semibold text-foreground"
                >
                  Search
                </Label>
                <Input
                  id="pes-tests-search"
                  value={searchTerm}
                  onChange={(event) => handleSearch(event.target.value)}
                  placeholder="Search by name..."
                  className="h-10"
                />
              </div>
            </CardContent>
          )}
        </Card>

        <Card className="py-0">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 px-4 py-3">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={(checked) =>
                        handleSelectAll(Boolean(checked))
                      }
                      aria-label="Select all tests in view"
                    />
                  </TableHead>
                  <TableHead className="px-4 py-3">
                    <Button
                      type="button"
                      variant="ghost"
                      className="flex w-full items-center justify-between gap-2 px-0 py-0 text-left text-sm font-semibold"
                      onClick={() => handleSort("Date")}
                    >
                      Test Time
                      {renderSortIcon("Date")}
                    </Button>
                  </TableHead>
                  <TableHead className="px-4 py-3">
                    <Button
                      type="button"
                      variant="ghost"
                      className="flex w-full items-center justify-between gap-2 px-0 py-0 text-left text-sm font-semibold"
                      onClick={() => handleSort("Name")}
                    >
                      Name
                      {renderSortIcon("Name")}
                    </Button>
                  </TableHead>
                  <TableHead className="px-4 py-3 text-sm font-semibold">
                    Type
                  </TableHead>
                  <TableHead>
                    <Button
                      type="button"
                      variant="ghost"
                      className="flex w-full items-center justify-between gap-2 px-0 py-0 text-left text-sm font-semibold"
                      onClick={() => handleSort("farm")}
                    >
                      Farm
                      {renderSortIcon("farm")}
                    </Button>
                  </TableHead>
                  <TableHead className="px-4 py-3">
                    <Button
                      type="button"
                      variant="ghost"
                      className="flex w-full items-center justify-between gap-2 px-0 py-0 text-left text-sm font-semibold"
                      onClick={() => handleSort("field")}
                    >
                      Field
                      {renderSortIcon("field")}
                    </Button>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 6 }).map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-4 w-20" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : transformedTests.length > 0 ? (
                  transformedTests.map((test) => {
                    const isSelected = selectedTestIds.has(test.id);
                    return (
                      <TableRow
                        key={test.id}
                        className={cn(
                          "cursor-pointer transition-colors hover:bg-muted/70 dark:hover:bg-accent/50",
                          isSelected && "bg-primary/10 dark:bg-primary/20"
                        )}
                        onClick={(e) => {
                          // Prevent navigation if the click originated from the checkbox or its container
                          // The checkbox cell is the first one.
                          if (
                            (e.target as HTMLElement).closest(
                              'button[role="checkbox"]'
                            )
                          ) {
                            return;
                          }
                          navigate(
                            getRoute(Routes.UserTestDetails, { id: test.id })
                          );
                        }}
                      >
                        <TableCell className="w-10 px-4 py-3">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleTestSelection(test)}
                            aria-label={`Select ${test.name}`}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell>
                          {format(
                            new Date(test.utc_test_time),
                            "dd/MM/yyyy HH:mm"
                          )}
                        </TableCell>
                        <TableCell>{test.name}</TableCell>
                        <TableCell>{test.test_type}</TableCell>
                        <TableCell>{test.farm?.name || "-"}</TableCell>
                        <TableCell>{test.field?.name || "-"}</TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center h-24 text-muted-foreground"
                    >
                      No tests found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {(page > 0 || hasNextPage) && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    if (page > 0) {
                      setPage((p) => p - 1);
                    }
                  }}
                  className={
                    page === 0 ? "pointer-events-none opacity-50" : undefined
                  }
                  aria-disabled={page === 0}
                />
              </PaginationItem>
              <PaginationItem>
                <span className="px-3 text-sm text-muted-foreground">
                  Page {page + 1}
                </span>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    if (hasNextPage) {
                      setPage((p) => p + 1);
                    }
                  }}
                  className={
                    !hasNextPage ? "pointer-events-none opacity-50" : undefined
                  }
                  aria-disabled={!hasNextPage}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      <Dialog open={isExportModalOpen} onOpenChange={setIsExportModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export Tests</DialogTitle>
            <DialogDescription>
              Select the format you want to export your tests in.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <RadioGroup
              value={exportFormat}
              onValueChange={(value) => setExportFormat(value as ReportFormat)}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={ReportFormat.Pdf} id="pdf" />
                <Label htmlFor="pdf">PDF</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={ReportFormat.Csv} id="csv" />
                <Label htmlFor="csv">CSV</Label>
              </div>
            </RadioGroup>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsExportModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={confirmExport} disabled={creatingReport}>
              {creatingReport ? "Exporting..." : "Export"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageTemplate>
  );
};

export default UserDashboard;
